// Outcome form state and its mapping to the log_call_outcome payload (spec 04 §3). Shared with Task 17.
import { zonedTimeToUtc } from "../../domain/torontoTime";
import { weekdayCallback } from "../../domain/time";
import type { CallOutcome } from "../../domain/dialer/types";
import type { OutcomePayload } from "../../domain/dialer/outcomes";

export type OutcomeFormState = {
  note: string;
  callback_local: string;
  demo_local: string;
  demo_duration: 30 | 45 | 60;
  attendee_email: string;
  /** GAVION demo-contact: « Nom du participant » (optional), the name of the contact created for the attendee. */
  attendee_name: string;
  amount: string;
  email: string;
  reason: string;
  reason_detail: string;
  dnc_confirmed: boolean;
  /** « A accepté de recevoir nos courriels » (B2 ruling): recorded as a consent, never sent in the payload. */
  email_consent: boolean;
};

export const emptyOutcomeForm = (): OutcomeFormState => ({
  note: "",
  callback_local: "",
  demo_local: "",
  demo_duration: 30,
  attendee_email: "",
  attendee_name: "",
  amount: "",
  email: "",
  reason: "",
  reason_detail: "",
  dnc_confirmed: false,
  email_consent: false,
});

/** "YYYY-MM-DDTHH:MM" in `tz` → ISO; undefined when empty or impossible. */
export function localToIso(local: string, tz: string): string | undefined {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(local);
  if (!m) return undefined;
  try {
    return zonedTimeToUtc(m[1], m[2], tz).toISOString();
  } catch {
    return undefined;
  }
}

/**
 * GAVION missed-call: for a callback typed on a Saturday, a Sunday, Dec 25 or Jan 1 (lead's zone), the ISO instant of
 * the next business day at the same time; undefined for a business day or an empty / impossible value.
 */
export function callbackWeekendSuggestion(
  local: string,
  tz: string,
): string | undefined {
  const iso = localToIso(local, tz);
  if (!iso) return undefined;
  const moved = weekdayCallback(new Date(iso), tz);
  return moved.getTime() === Date.parse(iso) ? undefined : moved.toISOString();
}

type Extras = Omit<OutcomePayload, "call_id" | "version" | "outcome">;

export function formToPayloadExtras(
  f: OutcomeFormState,
  outcome: CallOutcome,
  tz: string,
): Extras {
  const note = f.note.trim();
  const attendee = f.attendee_email.trim();
  const attendeeName = f.attendee_name.trim();
  const amount = f.amount.trim();
  const base: Extras = note ? { note } : {};
  const specific = ((): Extras => {
    switch (outcome) {
      case "talked_callback": {
        const at = localToIso(f.callback_local, tz);
        return at ? { callback_at: at } : {};
      }
      case "demo_booked": {
        const at = localToIso(f.demo_local, tz);
        return {
          demo: {
            ...(at ? { at } : {}),
            duration_min: f.demo_duration,
            ...(attendee ? { attendee_email: attendee } : {}),
            // GAVION demo-contact: the name only goes with an address (SQL creates no contact without one)
            ...(attendee && attendeeName
              ? { attendee_name: attendeeName }
              : {}),
            ...(amount && !Number.isNaN(Number(amount))
              ? { amount: Math.round(Number(amount)) }
              : {}),
          },
          ...(attendee ? { email: attendee } : {}),
        };
      }
      case "brochure_sent":
        return f.email.trim() ? { email: f.email.trim() } : {};
      case "not_interested":
        return {
          ...(f.reason ? { reason: f.reason } : {}),
          ...(f.reason_detail.trim()
            ? { reason_detail: f.reason_detail.trim() }
            : {}),
        };
      case "do_not_call":
        return f.dnc_confirmed ? { dnc_confirmed: true } : {};
      default:
        return {};
    }
  })();
  return { ...base, ...specific };
}

/** Outcomes where the rep spoke with the prospect about email: the consent checkbox is offered. */
export const CONSENT_OUTCOMES: readonly CallOutcome[] = [
  "talked_callback",
  "demo_booked",
  "brochure_sent",
];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The email to record an express consent for, or null (unchecked, not offered, or no valid email). */
export function consentEmail(
  f: OutcomeFormState,
  outcome: CallOutcome,
  leadEmail: string | null,
): string | null {
  if (!f.email_consent || !CONSENT_OUTCOMES.includes(outcome)) return null;
  const typed =
    outcome === "brochure_sent"
      ? f.email
      : outcome === "demo_booked"
        ? f.attendee_email
        : "";
  const email = (typed.trim() || leadEmail || "").trim();
  return EMAIL.test(email) ? email : null;
}
